import { useEffect, useRef, useState } from "react";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { Loaded } from "@/components/Loading";
import { Button } from "@/components/Button";
import { call, CALLABLES } from "@/services/callables";
import { requireEnv } from "@/lib/requireEnv";
import { SITE_EDITION } from "@/i18n";
import type { LegalBody, LegalDocument } from "@/types";

/**
 * A legal document, read before it is accepted.
 *
 * **Accept stays disabled until the text has been scrolled to the end.** It is
 * a low bar and it is not nothing: it means the person who accepted had the
 * whole document in front of them, and it makes the record we keep — name,
 * version, time — a claim we can stand behind.
 *
 * The HTML comes from the public site, fetched and reduced to an allow-list of
 * tags on the server. There is exactly one rendering of every document; a
 * second copy kept here would drift, and the drift is the failure that matters:
 * an institution reads one wording and is recorded as accepting another.
 */

/**
 * The end of the document is a sentinel, observed.
 *
 * The obvious implementation compares `scrollHeight` with `scrollTop +
 * clientHeight`. It has a hole: a dialog measures zero while it is still
 * hidden, so `0 - 0 <= tolerance` is true and the gate opens before anything
 * has been shown, let alone read. An `IntersectionObserver` cannot be satisfied
 * by an element nobody can see, which is exactly the property wanted here.
 */
const SENTINEL_MARGIN_PX = 24;

export function LegalReader({
  document,
  institutionName,
  onAccepted,
  onClose,
}: {
  document: LegalDocument;
  institutionName: string;
  onAccepted: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();

  const body = useCallable<LegalBody, { docId: string; locale: string }>(
    CALLABLES.getLegalDocumentBody,
    { docId: document.id, locale }
  );

  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const [busy, setBusy] = useState(false);

  /*
    A document short enough not to scroll is read to the end the moment it is
    displayed, and the observer reports exactly that — no special case, and no
    accept button that can never enable on a one-screen document.
  */
  useEffect(() => {
    const root = scroller.current;
    const end = sentinel.current;
    if (!root || !end || !body.data) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setReadToEnd(true);
      },
      { root, rootMargin: `0px 0px ${SENTINEL_MARGIN_PX}px 0px`, threshold: 0 }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [body.data]);

  const accept = async () => {
    setBusy(true);
    try {
      await call(CALLABLES.acceptLegalDocument, {
        docId: document.id,
        version: document.version,
      });
      success("legal.acceptedToast");
      onAccepted();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  const publicUrl = `${requireEnv("VITE_WEB_BASE_URL").replace(/\/$/, "")}/${SITE_EDITION[locale].prefix}/`;

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scroller}
        tabIndex={0}
        role="article"
        aria-label={document.title}
        className="legal-body card max-h-[60vh] min-h-48 overflow-y-auto px-5 py-4"
      >
        <Loaded state={body}>
          {(loaded) => (
            // The server reduced this to an allow-list of tags: no script, no
            // style, no attribute but a checked href. The allow-list is tested
            // in functions/src/__tests__/legalBody.test.ts.
            <>
              <div dangerouslySetInnerHTML={{ __html: loaded.html }} />
              {/* The end of the text, watched rather than calculated. */}
              <div ref={sentinel} aria-hidden="true" />
            </>
          )}
        </Loaded>
      </div>

      <p className="mt-4 text-sm text-ink-muted">
        {t("legal.acceptOnBehalf", { institution: institutionName })}
      </p>
      {!readToEnd && body.data ? (
        <p className="mt-1 text-sm text-warning">{t("legal.scrollToEnd")}</p>
      ) : null}

      <div className="mt-5 flex items-center justify-between gap-3">
        <a
          href={body.data?.url ?? publicUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
        >
          {t("legal.openOnSite")}
        </a>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button busy={busy} disabled={!readToEnd || body.data === undefined} onClick={accept}>
            {t("legal.accept")}
          </Button>
        </div>
      </div>
    </div>
  );
}
